import type { LucideIcon } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { cn } from '../lib/utils';
import { IconTile } from './icon-tile';

interface InspectorPanelProps {
  icon?: LucideIcon;
  /** Small upper-case line above the title: `STEP 1 · FILTER`. */
  eyebrow: ReactNode;
  title: string;
  /** Header buttons on the right: remove, collapse, close. */
  actions?: ReactNode;
  /** Pinned under the scrolling body: Reset / Apply, Publish. */
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * The frosted panel docked beside a canvas: header, a body that scrolls on
 * its own, and an optional pinned footer. Give it a height (or a flex parent)
 * so the body has something to scroll within.
 */
export const InspectorPanel: FC<InspectorPanelProps> = ({
  icon,
  eyebrow,
  title,
  actions,
  footer,
  children,
  className
}) => (
  <section
    aria-label={title}
    className={cn(
      'flex min-h-0 flex-col overflow-hidden rounded-[18px] border border-border border-t-border-strong bg-[var(--z-surface-overlay)] shadow-lg backdrop-blur-xl',
      className
    )}
  >
    <header className="flex items-center gap-3 border-b border-border px-6 py-5">
      {icon ? <IconTile icon={icon} tone="brand" size="md" /> : null}
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-eyebrow-sm text-muted-foreground uppercase">
          {eyebrow}
        </p>
        <h2 className="truncate text-subtitle text-foreground">{title}</h2>
      </div>
      {actions ? <div className="flex items-center gap-1">{actions}</div> : null}
    </header>
    {/* Focusable, so the body scrolls by keyboard even when it holds no controls. */}
    <div
      tabIndex={0}
      className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
    >
      {children}
    </div>
    {footer ? (
      <footer className="flex items-center gap-3 border-t border-border px-6 py-4">
        {footer}
      </footer>
    ) : null}
  </section>
);
